import { useSelector } from 'react-redux';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router';
import { paths } from '../../../../../../common/constants/pathVariables.js';
import { useTitle } from '../../../../../../common/hooks/useTitle.js';
import { omaElisaForEmployeeMsg, orderSimMsg, t, voiceSubscriptionsMsg } from '../../../../../../common/i18n/index.js';
import { getSubCategoryFromPath } from '../../../../../../common/utils/employeeUtils.js';
import { deepEqual } from '../../../../../../common/utils/objectUtils.js';
import { getSubscriptionStatusForEmployee } from '../../../../../../common/utils/subscriptionUtils.js';
import { BreadCrumbs } from '../../../../../../components/BreadCrumbs/index.js';
import { parseMobileSubscriptionAttrs } from '../../../../../../components/EoeSubscriptionDetails/eoeSubscriptionUtils.js';
import { Loading } from '../../../../../../components/Loading/index.js';
import { OrderSim } from '../../../../../../components/OrderSim/OrderSim.js';
import { SimType } from '../../../../../../generated/api/models.js';
import { useAuth } from '../../../../AuthProvider.js';
import { useSessionData } from '../../../../SessionDataProvider.js';
import type { SubscriptionId } from '../../../../../../common/constants/pathInterfaces.js';
import type { State } from '../../../../../../selfservice/common/store.js';
import type { JSX } from 'react';

export const EoeVoiceSubOrderSimPath = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const subCategoryFromPath = getSubCategoryFromPath(pathname);
  const { subscriptionId = '' } = useParams<SubscriptionId>();
  const { authenticatedUser } = useAuth();
  const subscription = useSelector((state: State) =>
    state?.user?.authenticated?.subscriptions?.voice?.find(sub => sub.subscriptionId === subscriptionId)
  );
  const companyInfo = useSessionData().companyInfo;
  const disallowBillableSimChanges = companyInfo?.disallowBillableSimChanges || false;
  const { selfService } = useSelector(
    (state: State) => ({
      selfService: state?.selfservice,
    }),
    deepEqual
  );

  const mobileNumber: string = subscription ? parseMobileSubscriptionAttrs(subscription).mobileNumber : '';

  const breadCrumbs: JSX.Element = (
    <BreadCrumbs
      breadCrumbPaths={[
        { name: t.H6JV(omaElisaForEmployeeMsg), path: paths.EMPLOYEE_HOME },
        { name: t.AR0B(voiceSubscriptionsMsg), path: paths.EMPLOYEE_VOICE_SUBSCRIPTIONS },
        { name: mobileNumber, path: generatePath(paths.EMPLOYEE_VOICE_SUBSCRIPTION, { subscriptionId }) },
        { name: t.CVON(orderSimMsg) },
      ]}
    />
  );

  useTitle([
    t.H6JV(omaElisaForEmployeeMsg),
    t.AR0B(voiceSubscriptionsMsg),
    subscription?.subscriptionDisplayId || subscription?.subscriptionId || '',
    t.CVON(orderSimMsg),
  ]);

  if (!(subscription && companyInfo)) {
    return <Loading />;
  }

  return (
    <div className="of-employee-subscriptions-scene">
      <OrderSim
        breadCrumbs={breadCrumbs}
        category={subCategoryFromPath}
        disallowBillableSimChanges={disallowBillableSimChanges}
        hasPendingActions={
          getSubscriptionStatusForEmployee(subscription, selfService?.pendingSubscriptionActions?.items || [])
            .hasPendingActions
        }
        simTypeInitialSelection={SimType.ESIM}
        subscription={subscription}
        companyInfo={companyInfo}
        user={authenticatedUser}
        onClickSubscription={() => navigate(generatePath(paths.EMPLOYEE_VOICE_SUBSCRIPTION, { subscriptionId }))}
        isEoe={true}
      />
    </div>
  );
};
